<template>
  <q-card flat bordered>
    <q-card-section class="row items-center justify-between">
      <div>
        <div class="text-h6 q-my-auto">Report Access</div>
        <div style="font-size: 12px">
          Edit the reports visible to your external parties in one place.
        </div>
      </div>
      <div class="row q-my-auto items-center q-gutter-x-sm">
        <q-btn icon="refresh" flat @click="refreshOrganizationReportAccesses" />
        <q-input
          v-model="organizationReportAccessesFilter"
          dense
          filled
          placeholder="Search People..."
        >
          <template #prepend>
            <q-icon name="search" />
          </template>
        </q-input>
        <q-btn
          id="intercom-assign-access"
          color="primary"
          unelevated
          icon="add"
          :label="$q.screen.gt.sm ? 'Assign Access' : ''"
          :loading="assignAccessSaving"
          no-caps
          @click="assignAccessDialog = true"
        />
      </div>
    </q-card-section>

    <q-markup-table v-if="organizationReportAccesses === null" bordered dense>
      <thead :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
        <tr>
          <th scope="col"></th>
          <th v-for="n in 5" :key="n" scope="col">
            <q-skeleton animation="blink" type="text" />
          </th>
          <th scope="col"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="n in 4" :key="n" style="height: 43px">
          <td style="width: 101px">
            <q-skeleton type="circle" style="width: 28px; height: 28px" />
          </td>
          <td v-for="x in 5" :key="x">
            <q-skeleton animation="blink" type="text" />
          </td>
          <td style="width: 99px">
            <div class="row justify-end">
              <q-skeleton animation="blink" type="circle" style="width: 28px; height: 28px" />
            </div>
          </td>
        </tr>
      </tbody>
    </q-markup-table>
    <q-table
      v-else
      flat
      dense
      bordered
      color="primary"
      row-key="id"
      :rows="organizationReportAccesses"
      :columns="memberColumns"
      :pagination="{
        rowsPerPage: 10
      }"
      :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
    >
      <template #no-data>
        <div class="q-mx-auto q-my-md text-center">
          <div class="text-subtitle2">No reports have been shared.</div>
        </div>
      </template>
      <template #body-cell-picture="{ row }">
        <q-td>
          <q-avatar size="28px" round class="q-mr-sm bg-grey-4">
            <q-img
              v-if="row.id !== row.email"
              :src="row.picture"
              referrerpolicy="no-referrer"
              no-spinner
            />
            <q-icon v-else name="mail_outline">
              <q-tooltip>Invite Sent</q-tooltip>
            </q-icon>
          </q-avatar>
        </q-td>
      </template>
      <template #body-cell-actions="{ row }">
        <q-td align="right">
          <q-btn icon="more_vert" dense flat>
            <q-menu transition-duration="100" max-width="170px" auto-close>
              <div>
                <q-btn
                  flat
                  no-caps
                  square
                  align="left"
                  class="full-width"
                  @click="displayEditReportAccess(row)"
                >
                  <q-icon name="edit" size="15px" class="q-mr-md" />
                  <div>Edit</div>
                </q-btn>
                <q-separator />
                <q-btn
                  flat
                  no-caps
                  square
                  align="left"
                  class="full-width"
                  @click="editReportAccessValidateDelete(row)"
                >
                  <q-icon name="delete" size="15px" class="q-mr-md" />
                  <div>Delete</div>
                </q-btn>
              </div>
            </q-menu>
          </q-btn>
        </q-td>
      </template>
    </q-table>
    <q-dialog
      v-model="editReportAccessDialog"
      transition-duration="100"
      @before-hide="clearAccessEdits"
    >
      <q-card style="width: 100%">
        <q-card-section class="row items-center justify-between">
          <div>
            You are editing access for <b>{{ editReportAccess?.email }}</b>
          </div>
          <property-report-search :report-ids="editReportAccessIds" @add="addEditReportAccess" />
        </q-card-section>
        <mini-property-reports-table
          :report-ids="editReportAccessIds"
          @remove="removeEditReportAccess"
        />
        <q-card-actions class="row justify-end q-gutter-x-sm">
          <i v-if="editReportAccessChanged" class="q-my-auto text-grey-7">Pending changes</i>
          <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
          <q-btn
            :disabled="!editReportAccessChanged"
            no-caps
            unelevated
            label="Save"
            color="primary"
            :loading="editReportAccessSaving"
            @click="saveReportAccessEdits"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <q-dialog
      v-model="assignAccessDialog"
      transition-duration="100"
      @before-hide="clearAssignAccessDialog"
    >
      <q-card style="max-width: 1200px" class="full-width">
        <q-card-section>
          <div class="text-h6">Bulk Assign</div>
        </q-card-section>
        <q-separator />
        <q-card-section class="row q-col-gutter-sm full-width">
          <div class="col-12 col-md-6 q-gutter-y-md">
            <property-report-search
              placeholder="Add reports by name or tag"
              :report-ids="assignAccessReportIds"
              @add="addAssignAccessReport"
            />
            <mini-property-reports-table
              style="height: 737px"
              :report-ids="assignAccessReportIds"
              @remove="removeAssignAccessReportId"
            />
          </div>
          <div class="col-12 col-md-6">
            <email-dropdown-select
              id="intercom-assign-access-people"
              v-model="assignAccessUsers"
              placeholder="Add people to the reports"
            />
          </div>
        </q-card-section>
        <q-separator />
        <q-card-actions class="row justify-end q-gutter-x-sm">
          <i
            v-if="assignAccessReportIds.length || assignAccessUsers.length"
            class="q-my-auto text-grey-7"
          >
            Pending changes
          </i>
          <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
          <q-btn
            :disabled="!assignAccessReportIds.length || !assignAccessUsers.length"
            no-caps
            unelevated
            label="Invite"
            color="primary"
            :loading="assignAccessSaving"
            @click="saveAssignAccess"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <q-dialog
      v-if="editReportAccessDeleteDialog"
      v-model="editReportAccessDeleteDialog"
      transition-duration="100"
    >
      <q-card style="width: 400px">
        <q-card-section class="row items-center">
          <span class="q-ml-sm">
            Are you sure you want to permanently remove
            <b>{{ editReportAccessToDelete?.email }}</b> from the
            <b>{{ editReportAccessToDelete?.report_ids.length || 0 }}</b> report{{
              (editReportAccessToDelete?.report_ids.length || 0) > 1 ? 's' : ''
            }}
            they currently have access to?
          </span>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
          <q-btn
            no-caps
            unelevated
            label="Delete"
            color="red"
            :loading="editReportAccessDeleteLoading"
            @click="editReportAccessDelete"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-card>
</template>

<script lang="ts">
import { type UserAttributes, type AccessUser } from 'common'
import { useUserStore } from '@/stores/user'
import { usePropertyReportStore } from '@/stores/propertyreport'
import { type QTableColumn } from 'quasar'
import { formatDate } from '@/utils'
import PropertyReportSearch from './PropertyReportSearch.vue'
import MiniPropertyReportsTable from './MiniPropertyReportsTable.vue'
import EmailDropdownSelect from '@/components/EmailDropdownSelect.vue'

export default {
  name: 'ReportAccess',
  components: {
    PropertyReportSearch,
    MiniPropertyReportsTable,
    EmailDropdownSelect
  },
  data() {
    return {
      organizationReportAccessesFilter: '',
      assignAccessDialog: false,
      assignAccessReportIds: [] as number[],
      assignAccessUsers: [] as AccessUser[],
      assignAccessSaving: false,
      editReportAccessDialog: false,
      editReportAccessChanged: false,
      editReportAccess: null as (UserAttributes & { report_ids: number[] }) | null,
      editReportAccessIds: [] as number[],
      editReportAccessSaving: false,
      editReportAccessDeleteDialog: false,
      editReportAccessToDelete: null as (UserAttributes & { report_ids: number[] }) | null,
      editReportAccessDeleteLoading: false
    }
  },
  computed: {
    activeSubscriptions() {
      return usePropertyReportStore().activeAdminSubscriptions
    },
    organizationReportAccesses() {
      const accesses = useUserStore().organizationReportAccesses
      if (this.organizationReportAccessesFilter && accesses) {
        return accesses.filter((_) =>
          [_.email, _.given_name, _.family_name]
            .filter((_) => _)
            .join(' ')
            .toLowerCase()
            .includes(this.organizationReportAccessesFilter.toLowerCase().trim())
        )
      }
      return accesses
    },
    organization() {
      return useUserStore().organization
    },
    organizationMembers() {
      return useUserStore().organizationMembers
    },
    memberColumns() {
      return [
        { field: 'picture', name: 'picture', label: '' },
        {
          field: 'given_name',
          name: 'given_name',
          label: 'First Name',
          sortable: true,
          align: 'left'
        },
        {
          field: 'family_name',
          name: 'family_name',
          label: 'Last Name',
          sortable: true,
          align: 'left'
        },
        { field: 'email', name: 'email', label: 'Email', sortable: true, align: 'left' },
        {
          field: (row) => row.report_ids.length,
          name: 'number_reports',
          label: '# Reports',
          sortable: true
        },
        {
          field: (row: UserAttributes) => {
            if (row.id === this.organization?.owner_user_id) {
              return 'Owner'
            } else if (this.organization?.admin_user_ids?.includes(row.id)) {
              return 'Admin'
            } else if (this.organizationMembers.some((_) => _.id === row.id)) {
              return 'Member'
            }
            return 'Guest'
          },
          name: 'role',
          label: 'Role',
          sortable: true,
          align: 'left'
        },
        {
          field: 'created_on',
          name: 'created_on',
          label: 'Created',
          sortable: true,
          align: 'left',
          format: formatDate
        },
        {
          name: 'actions',
          label: ''
        }
      ] as QTableColumn[]
    }
  },
  mounted() {
    if (!this.activeSubscriptions.length) {
      usePropertyReportStore().refreshAdminList()
    }
    if (!this.organizationReportAccesses?.length) {
      useUserStore().initOrganizationReportAccesses()
    }
  },
  methods: {
    editReportAccessValidateDelete(row: UserAttributes & { report_ids: number[] }) {
      this.editReportAccessDeleteDialog = true
      this.editReportAccessToDelete = row
    },
    displayEditReportAccess(row: UserAttributes & { report_ids: number[] }) {
      this.editReportAccessDialog = true
      this.editReportAccess = row
      this.editReportAccessIds = [...row.report_ids]
    },
    addEditReportAccess(id: number) {
      this.editReportAccessIds.push(id)
      this.editReportAccessChanged = true
    },
    clearAccessEdits() {
      this.editReportAccessDialog = false
      this.editReportAccess = null
      this.editReportAccessIds = []
      this.editReportAccessChanged = false
    },
    removeEditReportAccess(id: number) {
      this.editReportAccessIds = this.editReportAccessIds.filter((_) => _ !== id)
      this.editReportAccessChanged = true
    },
    saveReportAccessEdits() {
      if (this.editReportAccess?.id) {
        const currentIds = new Set(this.editReportAccess?.report_ids || [])
        const newIds = new Set(this.editReportAccessIds)
        const idsToDelete = Array.from(currentIds).filter((id) => !newIds.has(id))
        const idsToAdd = Array.from(newIds).filter((id) => !currentIds.has(id))
        const deletePromises = idsToDelete.map((id) => {
          return usePropertyReportStore().deleteAccess(id, this.editReportAccess!.id)
        })
        const userId =
          this.editReportAccess.id === this.editReportAccess.email
            ? null
            : this.editReportAccess!.id
        const addPromises = idsToAdd.map((id) => {
          return usePropertyReportStore()
            .createAccess(id, false, [
              {
                user_id: userId,
                email: this.editReportAccess!.email
              }
            ])
            .then(() => {
              return true
            })
        })
        this.editReportAccessSaving = true
        Promise.all(deletePromises.concat(addPromises)).finally(() => {
          useUserStore()
            .initOrganizationReportAccesses()
            .then(() => {
              this.editReportAccessSaving = false
              this.clearAccessEdits()
            })
        })
      }
    },
    clearAssignAccessDialog() {
      this.assignAccessDialog = false
      this.assignAccessReportIds = []
      this.assignAccessUsers = []
    },
    refreshOrganizationReportAccesses() {
      useUserStore().initOrganizationReportAccesses()
    },
    saveAssignAccess() {
      const users = this.assignAccessUsers.map((u) => ({
        user_id: u.id,
        email: u.email
      }))
      this.assignAccessSaving = true
      // We intentionally don't notify because otherwise we could send hundreds of emails per person.
      Promise.all(
        this.assignAccessReportIds.map((reportId) =>
          usePropertyReportStore().createAccess(reportId, false, users)
        )
      )
        .then(() => {
          this.refreshOrganizationReportAccesses()
          this.clearAssignAccessDialog()
        })
        .finally(() => {
          this.assignAccessSaving = false
        })
    },
    editReportAccessDelete() {
      if (this.editReportAccessToDelete) {
        this.editReportAccessDeleteLoading = true
        useUserStore()
          .reportAccessDelete(this.editReportAccessToDelete)
          .then(() => {
            this.editReportAccessDeleteLoading = false
            this.editReportAccessDeleteDialog = false
            this.editReportAccessToDelete = null
          })
      }
    },
    addAssignAccessReport(id: number) {
      this.assignAccessReportIds.push(id)
    },
    removeAssignAccessReportId(id: number) {
      this.assignAccessReportIds = this.assignAccessReportIds.filter((_) => _ !== id)
    }
  }
}
</script>
<style scoped lang="scss">
.width-150 {
  width: 150px;
}
</style>
